import { Show } from 'solid-js';
import type { StreamerData } from '../../../mocks/streamers/schema';

interface Props {
  streamer: StreamerData;
  state: 'minimal' | 'compact' | 'expanded';
  joined: boolean;
  multiplier: number;
  currentWin: number;
  mirrorDisabled: boolean;
  autoMirrorEnabled: boolean;
  autoMirrorRounds: number;
  autoMirrorRemaining: number;
  autoMirrorDisabled: boolean;
  onClose: () => void;
  onJoin: () => void;
  onMirror: () => void;
  onAutoMirrorToggle: (enabled: boolean) => void;
  onAutoMirrorRoundsChange: (value: number) => void;
  onCheer: () => void;
  onExpand: () => void;
  cheerPulse: boolean;
}

const statusLabel: Record<StreamerData['status'], string> = {
  LIVE: 'Live',
  AWAY: 'Away',
  OFFLINE: 'Offline',
};

export default function StreamerPanel(props: Props) {
  const bet = () => props.streamer.sampleBets[0];
  const autoCashout = () => bet().autoCashout;

  return (
    <div class="streamer-panel" data-state={props.state}>
      <div class="streamer-panel-header">
        <div class="streamer-panel-summary">
          <div class="streamer-avatar-wrap streamer-avatar-sm">
            <img src={props.streamer.avatar} alt="" aria-hidden="true" />
          </div>
          <div class="streamer-panel-meta">
            <div class="streamer-name-row">
              <span class="streamer-name">{props.streamer.displayName}</span>
              <span class="streamer-status-pill" data-status={props.streamer.status.toLowerCase()}>
                {statusLabel[props.streamer.status]}
              </span>
            </div>
            <div class="streamer-multiplier">
              <span class="streamer-mult-label">Live</span>
              <span class="streamer-mult-value">{props.multiplier.toFixed(2)}x</span>
            </div>
          </div>
        </div>
        <button class="streamer-icon-btn" type="button" aria-label="Close" onClick={props.onClose}>
          <span aria-hidden="true">×</span>
        </button>
      </div>

      <Show when={props.state === 'expanded'}>
        <div class="streamer-panel-body">
          <div class="streamer-kpi-grid">
            <div class="streamer-kpi">
              <span class="streamer-kpi-label">Streamer bet</span>
              <span class="streamer-kpi-value">{bet().amount.toFixed(2)} {bet().currency}</span>
              <Show when={autoCashout()}>
                <span class="streamer-kpi-sub">Auto @ {autoCashout()!.toFixed(2)}x</span>
              </Show>
            </div>
            <div class="streamer-kpi">
              <span class="streamer-kpi-label">Current win</span>
              <span class="streamer-kpi-value">{props.currentWin.toFixed(2)} {bet().currency}</span>
              <span class="streamer-kpi-sub">Multiplier {props.multiplier.toFixed(2)}x</span>
            </div>
            <div class="streamer-kpi">
              <span class="streamer-kpi-label">Room players</span>
              <span class="streamer-kpi-value">{props.streamer.roomStats.playersInRoom}</span>
              <span class="streamer-kpi-sub">Avg bet {props.streamer.roomStats.avgBet.toFixed(2)}</span>
            </div>
            <div class="streamer-kpi">
              <span class="streamer-kpi-label">Your rank</span>
              <span class="streamer-kpi-value">#{props.streamer.roomStats.userRank ?? '-'}</span>
              <span class="streamer-kpi-sub">By cumulative win</span>
            </div>
          </div>

          <div class="streamer-action-row">
            <button
              class="streamer-btn streamer-btn-primary"
              type="button"
              onClick={props.onMirror}
              disabled={!props.joined || props.mirrorDisabled}
              title="Copying streamer bets does not guarantee profit. Play responsibly."
            >
              Mirror bet
            </button>
            <button
              class={`streamer-btn streamer-btn-secondary ${props.cheerPulse ? 'cheer-pulse' : ''}`}
              type="button"
              onClick={props.onCheer}
            >
              Cheer
            </button>
          </div>
          <span class="streamer-action-tip" title="Copying streamer bets does not guarantee profit. Play responsibly.">
            Copying streamer bets does not guarantee profit. Play responsibly.
          </span>

          <div class="streamer-auto-mirror">
            <div class="streamer-auto-header">
              <label class="streamer-toggle">
                <input
                  type="checkbox"
                  checked={props.autoMirrorEnabled}
                  disabled={props.autoMirrorDisabled || !props.joined}
                  onInput={(e: InputEvent & { currentTarget: HTMLInputElement }) => props.onAutoMirrorToggle(e.currentTarget.checked)}
                />
                <span class="streamer-toggle-ui" aria-hidden="true"></span>
                <span>Auto-mirror next rounds</span>
              </label>
              <div class="streamer-auto-count">
                <input
                  type="number"
                  min="1"
                  max="5"
                  value={props.autoMirrorRounds}
                  disabled={!props.autoMirrorEnabled || props.autoMirrorDisabled || !props.joined}
                  onInput={(e: InputEvent & { currentTarget: HTMLInputElement }) => props.onAutoMirrorRoundsChange(Number(e.currentTarget.value))}
                />
                <span>rounds</span>
              </div>
            </div>
            <div class="streamer-auto-meta">
              <span>Remaining: {props.autoMirrorRemaining}</span>
              <span class="streamer-auto-limit">Auto-mirror capped at 20% of balance (UI-only).</span>
            </div>
            <Show when={props.autoMirrorDisabled}>
              <div class="streamer-auto-warning">Auto-mirror locked: streamer bet exceeds balance limit.</div>
            </Show>
          </div>

        </div>
      </Show>

      <Show when={props.state === 'compact'}>
        <div class="streamer-compact">
          <div class="streamer-compact-row">
            <span class="streamer-compact-label">Bet</span>
            <span class="streamer-compact-value">{bet().amount.toFixed(2)} {bet().currency}</span>
            <Show when={autoCashout()}>
              <span class="streamer-compact-sub">@ {autoCashout()!.toFixed(2)}x</span>
            </Show>
          </div>
          <div class="streamer-compact-row">
            <span class="streamer-compact-label">Status</span>
            <span class="streamer-compact-value">{statusLabel[props.streamer.status]}</span>
            <span class="streamer-compact-sub">{props.multiplier.toFixed(2)}x live</span>
          </div>
          <button class="streamer-btn streamer-btn-secondary streamer-compact-expand" type="button" onClick={props.onExpand}>
            Details
          </button>
        </div>
      </Show>

      <Show when={!props.joined && props.state === 'expanded'}>
        <div class="streamer-panel-join">
          <span>Join the streamer room to mirror bets and see full stats.</span>
          <button class="streamer-btn streamer-btn-primary" type="button" onClick={props.onJoin}>
            Join
          </button>
        </div>
      </Show>
    </div>
  );
}
